import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, TextField, Autocomplete, Button, IconButton, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Stack, Chip, Snackbar, Alert,
  CircularProgress, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, serverTimestamp, query, where, arrayUnion,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { siguienteNumeroPresupuesto } from '../../services/cajaService';
import { generatePresupuestoPDF } from '../../services/presupuestoPDF';
import { CLP, FINANCE_COLORS, netoDesdeBruto, ivaDesdeBruto, aplicarDescuentoPct, getArancelPrice, getArancelBranches, getConvenioDiscount, findConvenioByName } from '../../utils/finance';
import { addDaysLocal } from '../../utils/dateUtils';
import SearchableSelect from '../../components/SearchableSelect';

const { PRIMARY, TEAL, SUCCESS } = FINANCE_COLORS;

const addDays = (n) => addDaysLocal(n);
const emptyItem = () => ({ key: `it-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, prestacion: '', descripcion: '', arancelId: null, tipoPrestacion: '', unidadNegocio: '', cantidad: 1, precioUnitario: 0, descuento: 0, notas: '' });

const ESTADO_COLOR = { borrador: 'default', enviado: 'info', aprobado: 'success', rechazado: 'error', vencido: 'warning', cobrado: 'primary' };

export default function PresupuestoBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const { activeBranchId, activeBranch, filterByBranch } = useBranch();
  const isVet = ['médico veterinario', 'director médico'].includes((userData?.role || '').toLowerCase());

  const [patients, setPatients] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [selPatient, setSelPatient] = useState(null);
  const [validoHasta, setValidoHasta] = useState(addDays(30));
  const [items, setItems] = useState([emptyItem()]);
  const [notas, setNotas] = useState('');
  const [estado, setEstado] = useState('borrador');
  const [numero, setNumero] = useState('');
  const [origen, setOrigen] = useState({ orderId: null, orderType: '', orderNumber: '' });
  const [originalBranchId, setOriginalBranchId] = useState('');
  const [originalVetId, setOriginalVetId] = useState('');
  const [originalVetName, setOriginalVetName] = useState('');
  const [originalContactRequestId, setOriginalContactRequestId] = useState('');
  const [selectedVetId, setSelectedVetId] = useState('');
  const [docId, setDocId] = useState(id || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const contactRequestId = params.get('contactRequestId');

  const backUrl = useMemo(() => {
    const pid = params.get('patientId');
    if (contactRequestId) return `/contact-requests/${contactRequestId}`;
    if (pid) return `/patients/${pid}/record`;
    return '/presupuestos';
  }, [params, contactRequestId]);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Cargar catálogos + (opcional) presupuesto existente
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pSnap, oSnap, aSnap, cSnap, profSnap] = await Promise.all([
          getDocs(collection(db, 'patients')),
          getDocs(collection(db, 'owners')),
          getDocs(query(collection(db, 'aranceles'))),
          getDocs(collection(db, 'convenios')),
          getDocs(query(collection(db, 'professionals'), where('active', '!=', false))),
        ]);
        const ownersMap = {};
        oSnap.forEach((d) => { ownersMap[d.id] = { id: d.id, ...d.data() }; });
        const plist = pSnap.docs.map((d) => {
          const p = { id: d.id, ...d.data() };
          const o = ownersMap[p.ownerId] || {};
          return {
            patientId: p.id, name: p.name || '', species: p.species || '',
            ownerId: p.ownerId || '', ownerName: o.name || '', ownerPhone: o.celular || '', ownerRut: o.rut || '',
            ownerConvenio: o.convenio || '',
          };
        });
        setPatients(filterByBranch(plist, { includeUnassigned: true }));
        setConvenios(filterByBranch(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.isActive !== false), { includeUnassigned: true }));
        setAranceles(
          filterByBranch(aSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .filter((a) => a.activo !== false), { includeUnassigned: true })
            .sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es', { sensitivity: 'base' }))
        );
        setProfessionals(profSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        if (id) {
          const prSnap = await getDoc(doc(db, 'presupuestos', id));
          if (prSnap.exists()) {
            const pr = prSnap.data();
            setNumero(pr.numero || ''); setEstado(pr.estado || 'borrador');
            setValidoHasta(pr.validoHasta || addDays(30)); setNotas(pr.notas || '');
            setOrigen({ orderId: pr.orderId || null, orderType: pr.orderType || '', orderNumber: pr.orderNumber || '' });
            setOriginalBranchId(pr.branchId || '');
            setOriginalVetId(pr.vetId || '');
            setOriginalVetName(pr.vetName || '');
            setOriginalContactRequestId(pr.contactRequestId || '');
            setItems((pr.items || []).map((it, i) => {
              const prestacion = it.prestacion || it.descripcion || '';
              // El descuento se persiste como monto (CLP); en el builder se edita como %
              const cantidad = Number(it.cantidad) || 1;
              const sinDesc = (Number(it.precioUnitario) || 0) * cantidad;
              const descMonto = Number(it.descuento) || 0;
              const descPct = sinDesc > 0 ? Math.round((descMonto / sinDesc) * 100) : 0;
              return { ...emptyItem(), ...it, prestacion, descripcion: it.prestacion ? it.descripcion : '', descuento: descPct, key: `it-${i}` };
            }));
            setSelPatient(plist.find((p) => p.patientId === pr.patientId) || {
              patientId: pr.patientId, name: pr.patientName, species: pr.patientSpecies,
              ownerId: pr.ownerId, ownerName: pr.ownerName, ownerPhone: pr.ownerPhone,
            });
          }
        } else {
          const pid = params.get('patientId');
          if (pid) setSelPatient(plist.find((p) => p.patientId === pid) || null);
        }
      } catch (e) {
        notify('Error al cargar: ' + e.message, 'error');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (key) => setItems((p) => (p.length > 1 ? p.filter((i) => i.key !== key) : p));
  const updateItem = (key, patch) => setItems((p) => p.map((i) => i.key === key ? { ...i, ...patch } : i));

  const selConvenio = useMemo(() => findConvenioByName(convenios, selPatient?.ownerConvenio), [convenios, selPatient]);

  const onSelectArancel = (key, arancel) => {
    if (!arancel) { updateItem(key, { arancelId: null }); return; }
    const descPct = getConvenioDiscount(selConvenio, arancel.tipoPrestacionName || arancel.tipoPrestacion || '', arancel.id);
    updateItem(key, {
      arancelId: arancel.id, prestacion: arancel.prestacion, descripcion: '',
      precioUnitario: getArancelPrice(arancel, activeBranchId || userData?.branchId),
      tipoPrestacion: arancel.tipoPrestacionName || '', unidadNegocio: arancel.unidadNegocioName || '',
      descuento: descPct,
    });
  };

  const arancelesVisibles = useMemo(() => {
    const currentBranch = activeBranchId || userData?.branchId || '';
    return aranceles.filter((a) => {
      const branches = getArancelBranches(a);
      return branches.length === 0 || branches.includes(currentBranch);
    });
  }, [aranceles, activeBranchId, userData]);

  const totales = useMemo(() => {
    let bruto = 0, descuentoTotal = 0;
    items.forEach((i) => {
      const sinDesc = (Number(i.precioUnitario) || 0) * (Number(i.cantidad) || 1);
      const conDesc = aplicarDescuentoPct(sinDesc, i.descuento);
      bruto += conDesc; descuentoTotal += sinDesc - conDesc;
    });
    return { bruto: Math.round(bruto), neto: netoDesdeBruto(bruto), iva: ivaDesdeBruto(bruto), descuentoTotal };
  }, [items]);

  const buildData = (nuevoEstado) => ({
    numero: numero || '',
    orderId: origen.orderId || null, orderType: origen.orderType || '', orderNumber: origen.orderNumber || '',
    contactRequestId: originalContactRequestId || contactRequestId || null,
    patientId: selPatient?.patientId || '', patientName: selPatient?.name || '', patientSpecies: selPatient?.species || '',
    ownerId: selPatient?.ownerId || '', ownerName: selPatient?.ownerName || '', ownerPhone: selPatient?.ownerPhone || '',
    convenioId: selConvenio?.id || '', convenioName: selConvenio?.name || selPatient?.ownerConvenio || '',
    branchId: originalBranchId || activeBranchId || userData?.branchId || '',
    branch: activeBranch || {},
    vetId: selectedVetId || originalVetId || currentUser?.uid || '',
    vetName: (() => {
      if (selectedVetId) {
        const prof = professionals.find((p) => p.id === selectedVetId);
        if (prof) return `${prof.nombre || ''} ${prof.apellidos || ''}`.trim() || prof.email || '';
      }
      return originalVetName || userData?.name || currentUser?.email || '';
    })(),
    items: items.map((i) => {
      const sinDesc = (Number(i.precioUnitario) || 0) * (Number(i.cantidad) || 1);
      const total = aplicarDescuentoPct(sinDesc, i.descuento);
      return {
        prestacion: i.prestacion || i.descripcion, descripcion: i.descripcion, arancelId: i.arancelId || null, tipoPrestacion: i.tipoPrestacion || '',
        unidadNegocio: i.unidadNegocio || '', cantidad: Number(i.cantidad) || 1,
        precioUnitario: Number(i.precioUnitario) || 0, descuento: sinDesc - total, total, notas: i.notas || '',
      };
    }),
    subtotal: totales.neto, descuentoTotal: totales.descuentoTotal, iva: totales.iva, total: totales.bruto,
    validoHasta, estado: nuevoEstado, notas, updatedAt: serverTimestamp(),
  });

  const validar = () => {
    if (!selPatient) { notify('Selecciona un paciente', 'warning'); return false; }
    if (items.every((i) => !(i.prestacion || i.descripcion).trim())) { notify('Agrega al menos un ítem', 'warning'); return false; }
    return true;
  };

  const guardar = async (nuevoEstado) => {
    if (!validar()) return null;
    setSaving(true);
    try {
      let num = numero;
      if (!num) { num = await siguienteNumeroPresupuesto(); setNumero(num); }
      const data = { ...buildData(nuevoEstado), numero: num };
      let savedId = docId;
      if (docId) {
        await updateDoc(doc(db, 'presupuestos', docId), data);
      } else {
        const ref = await addDoc(collection(db, 'presupuestos'), { ...data, createdAt: serverTimestamp() });
        savedId = ref.id; setDocId(ref.id);
      }
      setEstado(nuevoEstado);

      // Vincular con el contact-request desde el que se originó
      const finalContactRequestId = originalContactRequestId || contactRequestId;
      if (finalContactRequestId && savedId && nuevoEstado === 'enviado') {
        try {
          const crRef = doc(db, 'contact-requests', finalContactRequestId);
          const crSnap = await getDoc(crRef);
          if (crSnap.exists()) {
            const cr = crSnap.data();
            const currentStatus = cr.status || 'Nuevo';
            const updates = { presupuestoIds: arrayUnion(savedId) };
            if (['Nuevo', 'Asignado', ''].includes(currentStatus)) {
              updates.status = 'Presupuesto Enviado';
              updates.logs = arrayUnion({
                userName: userData?.name || userData?.email || 'Usuario',
                userEmail: currentUser?.email || '',
                timestamp: new Date(),
                text: `Presupuesto enviado (N° ${num}) desde módulo de presupuestos`,
              });
            }
            await updateDoc(crRef, updates);
          }
        } catch (vincErr) {
          console.error('Error vinculando contact-request:', vincErr);
        }
      }

      notify('Presupuesto guardado');
      return { id: savedId, numero: num };
    } catch (e) {
      notify('Error al guardar: ' + e.message, 'error');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const vistaPrevia = async () => {
    if (!validar()) return;
    await generatePresupuestoPDF({ ...buildData(estado), numero: numero || 'BORRADOR' });
  };

  const enviar = async () => {
    const res = await guardar('enviado');
    if (res) await generatePresupuestoPDF({ ...buildData('enviado'), numero: res.numero });
  };

  const cobrar = async () => {
    if (['pagado', 'cobrado'].includes(estado)) return;
    let sid = docId;
    if (!['aprobado', 'abonado'].includes(estado)) { const r = await guardar('aprobado'); if (!r) return; sid = r.id; }
    navigate(`/caja/ticket?presupuestoId=${sid}`);
  };

  if (loading) return <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(backUrl)}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color={PRIMARY}>
            {docId ? `Presupuesto ${numero}` : 'Nuevo Presupuesto'}
          </Typography>
          {origen.orderNumber && (
            <Typography variant="caption" color="text.secondary">
              Generado desde orden {origen.orderNumber}
            </Typography>
          )}
        </Box>
        {selConvenio && <Chip label={`Convenio: ${selConvenio.name}`} color="primary" variant="outlined" />}
        <Chip label={estado} color={ESTADO_COLOR[estado] || 'default'} />
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Encabezado */}
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, mb: 2 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small" options={patients} value={selPatient}
                  onChange={(_, v) => setSelPatient(v)}
                  getOptionLabel={(o) => o?.name ? `${o.name} — ${o.ownerName}` : ''}
                  isOptionEqualToValue={(o, v) => o.patientId === v.patientId}
                  renderInput={(p) => <TextField {...p} label="Paciente" InputLabelProps={{ shrink: true }} />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth label="Propietario" value={selPatient?.ownerName || ''}
                  InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {isVet ? (
                  <TextField size="small" fullWidth label="Veterinario" value={originalVetName || userData?.name || currentUser?.email || ''}
                    InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                ) : (
                  <SearchableSelect size="small" fullWidth label="Veterinario *" value={selectedVetId || originalVetId || ''}
                    onChange={(e) => { setSelectedVetId(e.target.value); setOriginalVetId(''); setOriginalVetName(''); }}
                    options={professionals.map((p) => ({
                      value: p.id,
                      label: `${p.nombre || ''} ${p.apellidos || ''}`.trim() || p.email || '—',
                    }))}
                    emptyLabel="Seleccionar..." />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth type="date" label="Válido hasta" value={validoHasta}
                  onChange={(e) => setValidoHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Paper>

          {/* Ítems */}
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color={PRIMARY}>Ítems</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Agregar ítem</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem' } }}>
                  <TableCell>Prestación</TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>Cant.</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>P. Unit.</TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>Desc.%</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((i) => {
                  const sinDesc = (Number(i.precioUnitario) || 0) * (Number(i.cantidad) || 1);
                  const total = aplicarDescuentoPct(sinDesc, i.descuento);
                  return (
                    <TableRow key={i.key}>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Autocomplete
                          size="small" options={arancelesVisibles}
                          value={arancelesVisibles.find((a) => a.id === i.arancelId) || (i.arancelId ? { id: i.arancelId, prestacion: i.prestacion || i.descripcion } : null)}
                          onChange={(_, v) => onSelectArancel(i.key, v)}
                          getOptionLabel={(o) => o?.prestacion || ''}
                          renderInput={(p) => <TextField {...p} placeholder="Arancel" variant="standard" />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField variant="standard" type="number" value={i.cantidad}
                          onChange={(e) => updateItem(i.key, { cantidad: Math.max(1, Number(e.target.value) || 1) })}
                          inputProps={{ min: 1, style: { textAlign: 'center', width: 40 } }} />
                      </TableCell>
                      <TableCell align="right">
                        <TextField variant="standard" type="number" value={i.precioUnitario}
                          onChange={(e) => updateItem(i.key, { precioUnitario: Number(e.target.value) || 0 })}
                          inputProps={{ min: 0, style: { textAlign: 'right', width: 80 } }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField variant="standard" type="number" value={i.descuento}
                          onChange={(e) => updateItem(i.key, { descuento: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                          inputProps={{ min: 0, max: 100, style: { textAlign: 'center', width: 40 } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(total)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => removeItem(i.key)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TextField fullWidth multiline minRows={2} label="Notas generales" value={notas}
              onChange={(e) => setNotas(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mt: 2 }} />
          </Paper>
        </Grid>

        {/* Resumen + acciones */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, position: 'sticky', top: 80 }}>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} sx={{ mb: 1 }}>Resumen</Typography>
            {selConvenio && (
              <Chip label={`Convenio: ${selConvenio.name}`} color="primary" size="small" sx={{ mb: 1 }} />
            )}
            <Row label="Neto" value={CLP(totales.neto)} />
            <Row label="IVA (19%)" value={CLP(totales.iva)} />
            {totales.descuentoTotal > 0 && <Row label={`Descuentos ${selConvenio ? `(${selConvenio.name})` : ''}`} value={`-${CLP(totales.descuentoTotal)}`} />}
            <Divider sx={{ my: 1 }} />
            <Row label="TOTAL" value={CLP(totales.bruto)} bold />

            <Stack spacing={1} sx={{ mt: 2 }}>
              <Button fullWidth variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={() => guardar('borrador')} disabled={saving}>Guardar borrador</Button>
              <Button fullWidth variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={vistaPrevia}>Vista previa PDF</Button>
              <Button fullWidth variant="contained" startIcon={<SendIcon />} onClick={enviar} disabled={saving} sx={{ bgcolor: TEAL }}>
                Enviar al propietario
              </Button>
              <Button fullWidth variant="outlined" color="success" startIcon={<CheckIcon />}
                onClick={() => guardar('aprobado')} disabled={saving}>Marcar aprobado</Button>
              <Button fullWidth variant="contained" startIcon={<PointOfSaleIcon />} onClick={cobrar} disabled={saving || ['pagado','cobrado'].includes(estado)}
                sx={{ bgcolor: SUCCESS, '&:hover': { bgcolor: '#0e9e6e' } }}>{['pagado','cobrado'].includes(estado) ? 'Presupuesto pagado' : (estado === 'abonado' ? 'Pagar saldo' : 'Cobrar este presupuesto')}</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

function Row({ label, value, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
      <Typography variant={bold ? 'body1' : 'body2'} fontWeight={bold ? 800 : 400} color={bold ? 'text.primary' : 'text.secondary'}>{label}</Typography>
      <Typography variant={bold ? 'h6' : 'body2'} fontWeight={bold ? 800 : 600} color={bold ? PRIMARY : 'text.primary'}>{value}</Typography>
    </Box>
  );
}
